import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { UserLanguage } from '../../features/account/types/User'
import { applyFeatureEffectsChange } from '../features/motivations/helpers/featureEffectHelpers'
import { applyMotivationNamesChange, motivationLanguages, motivationsOf } from '../features/motivations/helpers/motivationLocalizationHelpers'
import { hasMotivationAnalysis } from '../features/motivations/helpers/motivationOverrideHelpers'
import {
  FeatureEffectsChange,
  FeatureEffectsMap,
  MotivationAnalysis,
  MotivationNamesChange,
  MotivationOverride,
  SavedFeatureEffects,
  SavedMotivationNames,
} from '../features/motivations/types/Motivations'
import { internalApiserverApi } from './internal-apiserver'
import { internalConnectApi } from './internal-connect'
import { LanguageMaps } from './language'

const toAnalysis = (analysis: MotivationAnalysis | null | undefined) => (hasMotivationAnalysis(analysis) ? analysis : null)

export const motivationNamesFailedError = 'MOTIVATION_NAMES_FAILED'

export type MotivationNamesFailure = { savedLanguages: UserLanguage[]; failedLanguages: UserLanguage[] }

/** The languages a failed `saveMotivationNames` saved and didn't save; undefined for a failure before any write. */
export const getMotivationNamesFailure = (error: unknown): MotivationNamesFailure | undefined => {
  const candidate = error as { status?: unknown; error?: unknown; data?: MotivationNamesFailure } | null
  return candidate?.status === 'CUSTOM_ERROR' && candidate.error === motivationNamesFailedError ? candidate.data : undefined
}

const motivationAnalysisApi = internalConnectApi.enhanceEndpoints({ addTagTypes: ['motivationAnalysis'] }).injectEndpoints({
  endpoints: (builder) => ({
    // `null` when the game has no analysis.
    getMotivationAnalysis: builder.query<MotivationAnalysis | null, number>({
      query: (appId) => `motivation/analysis/${appId}`,
      transformResponse: toAnalysis,
      providesTags: (_result, _error, appId) => [{ type: 'motivationAnalysis', id: appId }],
    }),
    // The backend also recalculates the game's segments, even when the values are unchanged. Returns the analysis as stored afterwards.
    overrideMotivationAnalysis: builder.mutation<MotivationAnalysis | null, MotivationOverride>({
      async queryFn(override, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: 'i/motivation/analysis/override', method: 'POST', body: override, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const analysis = await baseQuery(`motivation/analysis/${override.appId}`)
        if (analysis.error) return { error: analysis.error }
        return { data: toAnalysis(analysis.data as MotivationAnalysis) }
      },
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'motivationAnalysis', id: appId }],
    }),
  }),
})

const motivationsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['featureEffects', 'languageMaps'] }).injectEndpoints({
  endpoints: (builder) => ({
    getFeatureEffects: builder.query<FeatureEffectsMap, void>({
      query: () => 'features_effects',
      providesTags: ['featureEffects'],
    }),
    // The change is applied to a fresh read, and the whole map is sent as legacy does.
    saveFeatureEffects: builder.mutation<SavedFeatureEffects, FeatureEffectsChange>({
      async queryFn(change, _api, _extraOptions, baseQuery) {
        const current = await baseQuery('features_effects')
        if (current.error) return { error: current.error }
        const expected = applyFeatureEffectsChange((current.data as FeatureEffectsMap) ?? {}, change)
        const saved = await baseQuery({ url: 'i/features_effects', method: 'POST', body: expected, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery('features_effects')
        if (stored.error) return { error: stored.error }
        return { data: { expected, stored: (stored.data as FeatureEffectsMap) ?? {} } }
      },
      invalidatesTags: ['featureEffects'],
    }),
    // `PUT i/language/` replaces the groups it stores, so each language gets its complete, freshly read document with only `motivations` changed.
    // Every language is written even when one fails, and a failure names the languages that weren't saved. Invalidates
    // on failure too, as the other languages may have been saved.
    saveMotivationNames: builder.mutation<SavedMotivationNames, MotivationNamesChange>({
      async queryFn(change, _api, _extraOptions, baseQuery) {
        const current = await baseQuery('language')
        if (current.error) return { error: current.error }
        const languageMaps = current.data as LanguageMaps
        const expected = applyMotivationNamesChange(languageMaps, change)
        const failedLanguages: UserLanguage[] = []
        for (const language of motivationLanguages) {
          const saved = await baseQuery({ url: 'i/language/', method: 'PUT', body: { ...languageMaps[language], motivations: expected[language] } })
          if (saved.error) failedLanguages.push(language)
        }
        if (failedLanguages.length) {
          const failure: MotivationNamesFailure = {
            savedLanguages: motivationLanguages.filter((language) => !failedLanguages.includes(language)),
            failedLanguages,
          }
          return { error: { status: 'CUSTOM_ERROR', error: motivationNamesFailedError, data: failure } }
        }
        const stored = await baseQuery('language')
        if (stored.error) return { error: stored.error as FetchBaseQueryError }
        const storedMaps = stored.data as LanguageMaps
        return { data: { expected, stored: Object.fromEntries(motivationLanguages.map((language) => [language, motivationsOf(storedMaps[language])])) } }
      },
      invalidatesTags: ['languageMaps'],
    }),
  }),
})

export const { useGetMotivationAnalysisQuery, useOverrideMotivationAnalysisMutation } = motivationAnalysisApi
export const { useGetFeatureEffectsQuery, useSaveFeatureEffectsMutation, useSaveMotivationNamesMutation } = motivationsApi
